import { Suspense, useCallback, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import * as Avatar from '@givebutter/primitives/avatar';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal } from '@givebutter/util';
import {
    faBuilding,
    faFileExport,
    faFileImport,
    faHeart,
    faPlus,
    faRefresh,
} from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartFilled } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Content from 'assets/js/components/Content';
import { getComputedContactProps } from 'assets/js/pages/contacts/transform';
import { CreateTransactionDialog } from 'assets/js/pages/transactions/CreateTransactionDialog';
import { ExportTransactionsDialog } from 'assets/js/pages/transactions/ExportTransactionsDialog';
import { TransactionMethodPresentational } from 'assets/js/pages/transactions/TransactionMethod';
import { TransactionRecurringPlanBadge } from 'assets/js/pages/transactions/TransactionRecurringPlanBadge';
import { TransactionStatusBadge } from 'assets/js/pages/transactions/TransactionStatusBadge';
import { TransactionsTable } from 'assets/js/pages/transactions/TransactionsTable';
import { TransactionsTableProvider } from 'assets/js/pages/transactions/TransactionsTableProvider';
import { useToggleAcknowledge } from 'assets/js/pages/transactions/useToggleAcknowledge';
import { curriedTransformTableKeyToReadable } from 'assets/js/pages/transactions/util';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Import from 'components/imports/Import';
import * as SubNav from 'components/sub-nav';
import { TransactionsSubNavTabs } from 'components/sub-nav/TransactionsSubNavTabs';
import * as Table from 'components/table';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import * as Tooltip from 'components/tooltip';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { ContactType } from 'constants/contacts';
import { IMPORT_TRANSACTIONS_TEMPLATE_LINK } from 'constants/links';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { inlineAddress } from 'helpers/address';
import { rawDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { Contact } from 'types/domain';

const Transactions = () => {
    const history = useHistory();
    const { state: locationState } = useLocation<{ openExportDialog?: boolean }>();
    const { accountId } = useCurrentAccount();
    const [showExportModal, setShowExportModal] = useState(
        locationState?.openExportDialog ?? false,
    );
    const [createDonationCampaignId, setCreateDonationCampaignId] = useState<string | undefined>();

    const NewTransactionButton = useCallback(
        () => (
            <Button
                variant={'solid'}
                color={'primary'}
                onClick={() =>
                    history.push({
                        pathname: `/accounts/${accountId}/transactions/new`,
                    })
                }
            >
                <FontAwesomeIcon icon={faPlus} />
                New transaction
            </Button>
        ),
        [accountId, history],
    );

    return (
        <div className={'flex flex-col gap-y-4'}>
            <SubNav.Root>
                <SubNav.Header>
                    <SubNav.Title>Transactions</SubNav.Title>
                    <SubNav.Actions>
                        <Button
                            variant="outline"
                            color="neutral"
                            onClick={() => setShowExportModal(true)}
                        >
                            <FontAwesomeIcon icon={faFileExport} />
                            Export
                        </Button>
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild={true}>
                                <Button variant="outline" color="neutral">
                                    <FontAwesomeIcon icon={faFileImport} />
                                    Import
                                </Button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <Suspense
                                    fallback={
                                        <DropdownMenu.Item
                                            disabled={true}
                                            className="grid grid-cols-[16px_1fr] items-center gap-2"
                                        >
                                            <FontAwesomeIcon icon={faFileImport} />
                                            Transactions import
                                        </DropdownMenu.Item>
                                    }
                                >
                                    <Import.Root strategy="transactions">
                                        <Import.Trigger asChild={true}>
                                            <DropdownMenu.Item
                                                onClick={e => e.preventDefault()}
                                                className="grid grid-cols-[16px_1fr] items-center gap-2"
                                            >
                                                <FontAwesomeIcon icon={faFileImport} />
                                                Transactions import
                                            </DropdownMenu.Item>
                                        </Import.Trigger>
                                        <Import.Portal>
                                            <Import.Space />
                                        </Import.Portal>
                                    </Import.Root>
                                </Suspense>
                                <DropdownMenu.Item>
                                    <a
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-gray-900"
                                        href={IMPORT_TRANSACTIONS_TEMPLATE_LINK}
                                    >
                                        Download import template
                                    </a>
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    onClick={() =>
                                        history.push(`/accounts/${accountId}/settings/imports`)
                                    }
                                >
                                    View import history
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                        <NewTransactionButton />
                    </SubNav.Actions>
                </SubNav.Header>
                <TransactionsSubNavTabs />
            </SubNav.Root>
            <TransactionsTable>
                <div className="mt-5 flex flex-wrap justify-center gap-3 sm:flex-nowrap">
                    <Suspense fallback={null}>
                        <Import.Root strategy="transactions">
                            <Import.Trigger asChild={true}>
                                <Button
                                    className="flex items-center gap-1.5"
                                    variant={'outline'}
                                    color={'neutral'}
                                >
                                    <FontAwesomeIcon icon={faFileImport} />
                                    Import transactions
                                </Button>
                            </Import.Trigger>
                            <Import.Space />
                        </Import.Root>
                    </Suspense>
                    <NewTransactionButton />
                </div>
            </TransactionsTable>
            <ExportTransactionsDialog
                autoCloseOnSuccess={false}
                open={showExportModal}
                onOpenChange={setShowExportModal}
                onExportSuccess={() => setShowExportModal(false)}
            />
            {createDonationCampaignId && (
                <Suspense fallback={null}>
                    <CreateTransactionDialog
                        open={!!createDonationCampaignId}
                        onOpenChange={open => {
                            if (!open) {
                                setCreateDonationCampaignId(undefined);
                            }
                        }}
                        campaignId={parseInt(createDonationCampaignId)}
                    />
                </Suspense>
            )}
        </div>
    );
};

const TransactionsWrapper = () => {
    const { hasIntegrations, timezone, accountId } = useCurrentAccount();
    const { mutate: toggleAcknowledge } = useToggleAcknowledge();
    const currencySymbol = useCurrencySymbol();
    const { transactionSoftCredits } = useFlags();

    return (
        <TableCoreResourceProvider
            resource={'transactions'}
            initialState={{
                sortingState: [{ id: 'transacted_at', desc: true }],
                columnVisibilityState: {
                    [Table.Constants.Actions]: true,
                    first_name: true,
                    amount: true,
                    campaign: true,
                    transacted_at: true,
                    status_friendly: true,
                    email: false,
                    company: false,
                    phone: false,
                    address: false,
                    address_address_1: false,
                    address_address_2: false,
                    address_city: false,
                    address_state: false,
                    address_zipcode: false,
                    address_country: false,
                    external_id: false,
                    external_label: false,
                    fund: false,
                    team: false,
                    team_member: false,
                    discount: false,
                    check_number: false,
                    check_deposited_at: false,
                    payment_method: true,
                    soft_credits: false,
                    internal_note: false,
                    public_message: false,
                },
                columnOrderState: [
                    'contact_name',
                    'amount',
                    'campaign',
                    'transacted_at',
                    'status_friendly',
                    'payment_method',
                ],
                columnPinningState: { right: [Table.Constants.Actions] },
            }}
            columns={columnHelper => [
                columnHelper.accessor('first_name', {
                    id: 'contact_name',
                    header: curriedTransformTableKeyToReadable,
                    enableHiding: false,
                    cell: info => {
                        const { contact } = info.row.original;

                        if (!contact) {
                            return <EmptyData />;
                        }

                        const contactProps = getComputedContactProps(contact);

                        return (
                            <Link to={contactProps.linkUrl} className={'font-medium text-black'}>
                                <div className="flex gap-2">
                                    {contactProps.contactName}
                                    {contactProps.isCompany && (
                                        <Badge size="sm" color="success">
                                            <FontAwesomeIcon icon={faBuilding} /> Company
                                        </Badge>
                                    )}
                                </div>
                            </Link>
                        );
                    },
                    meta: {
                        ignoreLink: true,
                    },
                }),
                columnHelper.accessor('campaign', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => (
                        <div className={'flex max-w-[200px]'}>
                            <TruncatedText>{info.getValue().title}</TruncatedText>
                        </div>
                    ),
                }),
                columnHelper.accessor('amount', {
                    header: curriedTransformTableKeyToReadable,
                    cell: info => {
                        const transaction = info.row.original;

                        return (
                            <div className={'flex items-center gap-2'}>
                                <p>
                                    {currencySymbol}
                                    {maskDecimal(info.getValue())}
                                </p>
                                <TransactionRecurringPlanBadge transaction={transaction} />
                            </div>
                        );
                    },
                    meta: {
                        sortType: 'numeric',
                    },
                }),
                columnHelper.accessor('transacted_at', {
                    header: () => (
                        <div className={'flex gap-1'}>
                            <p>Date</p>
                            <p className={'text-xs leading-5 text-gray-400'}>
                                ({DateTime.now().setZone(timezone).offsetNameShort})
                            </p>
                        </div>
                    ),
                    cell: info =>
                        rawDateFromServer(info.getValue())
                            .setZone(timezone)
                            .toLocaleString(DateTime.DATE_FULL),
                    meta: {
                        sortType: 'date',
                    },
                }),
                columnHelper.accessor('status_friendly', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const transaction = info.row.original;

                        return <TransactionStatusBadge transaction={transaction} />;
                    },
                }),
                columnHelper.accessor('email', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('phone', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const phone = info.getValue();

                        return (
                            <p className="whitespace-nowrap">
                                {phone ? getFormattedPhoneNumber(phone) : <EmptyData />}
                            </p>
                        );
                    },
                }),
                columnHelper.accessor('company', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('payment_method', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const paymentMethod = info.getValue();

                        if (!paymentMethod || paymentMethod.method === 'none') {
                            return <EmptyData />;
                        }

                        return <TransactionMethodPresentational transaction={info.row.original} />;
                    },
                }),
                columnHelper.accessor('external_ids', {
                    id: 'external_id',
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const externalId = info.getValue()?.[0]?.external_id;

                        return <p>{externalId ?? <EmptyData />}</p>;
                    },
                }),
                columnHelper.accessor('external_ids', {
                    id: 'external_label',
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const externalLabel = info.getValue()?.[0]?.label;

                        return <p>{externalLabel ?? <EmptyData />}</p>;
                    },
                }),
                columnHelper.accessor('fund', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const fund = info.getValue();

                        if (fund) {
                            return <p>{fund.name}</p>;
                        }

                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('team', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const team = info.getValue();

                        if (team) {
                            return <p>{team.name}</p>;
                        }

                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('team_member', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const teamMember = info.getValue();

                        if (teamMember) {
                            return <p>{teamMember.display_name}</p>;
                        }

                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('address', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => <p>{inlineAddress(info.getValue())}</p>,
                }),
                columnHelper.accessor('address.address_1', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('address.address_2', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('address.city', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('address.state', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('address.zipcode', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('address.country', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('discount', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const promoCode = info.getValue();

                        if (promoCode?.code) {
                            return <p>{promoCode.code}</p>;
                        }

                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('check_number', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: () => <Table.CellDataOrEmpty />,
                }),
                columnHelper.accessor('check_deposited_at', {
                    header: () => (
                        <div className={'flex gap-1'}>
                            <p>Check deposit date</p>
                            <p className={'text-xs leading-5 text-gray-400'}>
                                ({DateTime.now().setZone(timezone).offsetNameShort})
                            </p>
                        </div>
                    ),
                    enableSorting: false,
                    cell: info => {
                        const checkDepositedAt = info.getValue();

                        if (checkDepositedAt) {
                            return rawDateFromServer(checkDepositedAt)
                                .setZone(timezone)
                                .toLocaleString(DateTime.DATE_FULL);
                        }

                        return <EmptyData />;
                    },
                }),
                columnHelper.accessor('internal_note', {
                    header: curriedTransformTableKeyToReadable,
                    enableSorting: false,
                    cell: info => {
                        const note = info.getValue();

                        if (!note) {
                            return <EmptyData />;
                        }

                        return (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <p className="max-w-64 truncate">{note}</p>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>{note}</p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        );
                    },
                }),
                columnHelper.display({
                    header: curriedTransformTableKeyToReadable,
                    id: 'public_message',
                    cell: info => {
                        const { giving_space } = info.row.original;

                        if (!giving_space?.message) {
                            return <EmptyData />;
                        }

                        return (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <p className="max-w-64 truncate">{giving_space.message}</p>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>{giving_space.message}</p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        );
                    },
                }),
                columnHelper.display({
                    id: Table.Constants.Actions,
                    cell: info => {
                        let color: string;
                        let bgColor: string;
                        let integrationText: string;
                        const transaction = info.row.original;
                        const status = transaction.integration_status;

                        const isAcknowledged = !!transaction.acknowledged_at;

                        switch (status) {
                            case 'pending':
                                color = 'text-yellow-500';
                                bgColor = 'bg-yellow-100';
                                integrationText = 'Integration sync pending';
                                break;
                            case 'failed':
                                color = 'text-red-500';
                                bgColor = 'bg-red-100';
                                integrationText = 'Integration sync failed';
                                break;
                            case 'succeeded':
                            default:
                                color = 'text-green-700';
                                bgColor = 'bg-green-100';
                                integrationText = 'Integration sync successful';
                        }

                        return (
                            <div className={'flex items-center gap-x-1'}>
                                {hasIntegrations && !!status && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <Avatar.Root size="2xs">
                                                <Avatar.Fallback
                                                    className={bgColor}
                                                    aria-label="Integration sync status"
                                                >
                                                    <FontAwesomeIcon
                                                        className={color}
                                                        icon={faRefresh}
                                                    />
                                                </Avatar.Fallback>
                                            </Avatar.Root>
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content className={'!p-3'} inverse={true}>
                                                <p>{integrationText}</p>
                                                <Tooltip.Arrow inverse={true} />
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                )}
                                <Tooltip.Root>
                                    <Tooltip.Trigger asChild={true}>
                                        <Button
                                            variant="transparent"
                                            aria-label={'Toggle acknowledge button'}
                                            shape="square"
                                            size="sm"
                                            onClick={e => {
                                                e.stopPropagation();
                                                toggleAcknowledge(transaction);
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={isAcknowledged ? faHeartFilled : faHeart}
                                            />
                                        </Button>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content className={'!p-3'} inverse={true}>
                                            <p>
                                                {isAcknowledged ? 'Unacknowledge' : 'Acknowledge'}{' '}
                                                transaction for internal tracking. This doesn't
                                                alter the transaction, notify the donor, or become
                                                visible to the public.
                                            </p>
                                            <Tooltip.Arrow inverse={true} />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            </div>
                        );
                    },
                    meta: {
                        ignoreLink: true,
                        className: '[&>*]:justify-center !px-4',
                        headerClassName: '[&>*]:w-full [&>*]:justify-center',
                    },
                }),
                ...(transactionSoftCredits
                    ? [
                          columnHelper.accessor('soft_credits', {
                              header: curriedTransformTableKeyToReadable,
                              enableSorting: false,
                              cell: info => {
                                  const softCredits = info.getValue();

                                  if (!softCredits.length) {
                                      return <EmptyData />;
                                  }

                                  const maxDisplayed = 2;
                                  const displayedItems = softCredits.slice(0, maxDisplayed);
                                  const hiddenItems = softCredits.slice(maxDisplayed);

                                  const SoftCreditLink = ({ contact }: { contact: Contact }) => {
                                      const name =
                                          contact.type === ContactType.Company
                                              ? contact.company_name
                                              : `${contact.first_name} ${contact.last_name}`;
                                      const resource =
                                          contact.type === ContactType.Company
                                              ? 'companies'
                                              : 'contacts';
                                      return (
                                          <Link
                                              to={`/accounts/${accountId}/${resource}/${contact.id}`}
                                          >
                                              {name}
                                          </Link>
                                      );
                                  };

                                  return (
                                      <span>
                                          {displayedItems.map(({ contact }, index) => {
                                              return (
                                                  <span key={contact.id}>
                                                      {index > 0 && ', '}
                                                      <SoftCreditLink contact={contact} />
                                                  </span>
                                              );
                                          })}
                                          {hiddenItems.length > 0 && (
                                              <span>
                                                  {`, `}
                                                  <Tooltip.Root>
                                                      <Tooltip.Trigger>
                                                          <span>and {hiddenItems.length} more</span>
                                                      </Tooltip.Trigger>
                                                      <Tooltip.Portal>
                                                          <Tooltip.Content inverse>
                                                              <Tooltip.Arrow inverse />
                                                              <div className="flex flex-col">
                                                                  {hiddenItems.map(
                                                                      ({ contact }) => (
                                                                          <SoftCreditLink
                                                                              key={contact.id}
                                                                              contact={contact}
                                                                          />
                                                                      ),
                                                                  )}
                                                              </div>
                                                          </Tooltip.Content>
                                                      </Tooltip.Portal>
                                                  </Tooltip.Root>
                                              </span>
                                          )}
                                      </span>
                                  );
                              },
                          }),
                      ]
                    : []),
            ]}
            options={{
                enableHiding: true,
                enableSorting: true,
                enablePinning: true,
                enableExpanding: true,
                meta: {
                    enableColumnOrder: true,
                },
            }}
        >
            <TransactionsTableProvider>
                <Content>
                    <Transactions />
                </Content>
            </TransactionsTableProvider>
        </TableCoreResourceProvider>
    );
};

export { TransactionsWrapper as Transactions };
